import { Component, OnInit, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { DataManagementService } from '../../data-management.service';
import { MissionService } from '../../providers';
import * as XLSX from 'xlsx';
import { Mission, RapportDto } from '../../data-management.model';
import { saveAs as importedSaveAs } from 'file-saver';
import { MissionSuiviRequest, PlanificationAssignmentAddDto } from '..';
import { Truck } from './planification.model';
import { PlanificationService } from '../planification.service';
import { ImportExportService } from 'src/app/client-management/import-export/import-export.service';

interface Vehicule {
    idVehicule: number;
    matricule: string;
    subMark: string;
}

interface Group {
    idGroupe: number;
    nom: string;
    nameGroupParent?: string;
    vehicules: Vehicule[];
}

interface Planning {
    truckId: number | null;
    label: string;
    idVehicule: number | null;
    missions: { [key: string]: number | null };
}

@Component({
    selector: 'app-planification-mission-form',
    templateUrl: './planification-mission.component.html',
    styleUrls: ['./planification-mission.component.scss']
})
export class PlanificationMissionComponent implements OnInit {
    @ViewChild('planningModal', { static: false }) planningModal!: ModalDirective;
    @ViewChild('truckDeleteModal', { static: false }) truckDeleteModal!: ModalDirective;
    @ViewChild('truckGenerateModal', { static: false }) truckGenerateModal!: ModalDirective;

    planificationAssignmentAddDto: PlanificationAssignmentAddDto = {
        dateDebut: undefined,
        dateFin: undefined,
        id: 0,
        idVehicule: 0,
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        label: '',
    };
    loading = false;
    loader = false;
    trucks: Truck[] = [];
    selectedTruck: Truck | null = null;
    availableMissions: Mission[] = [];
    days: string[] = ['J1', 'J2'];
    newPlanning: Planning = { truckId: null, label: '', idVehicule: null, missions: {} };
    mode: 'ADD' | 'UPDATE' = 'ADD';
    vehicules: Vehicule[] = [];
    groups: Group[] = [];
    vehiculesItems: { id: number; matricule: string; subMark: string }[] = [];
    vehiculesSelected: { id: number; matricule: string; subMark: string } | null = null;
    groupsItems: { id: number; text: string }[] = [];
    selectedGroupsItems: { id: number; text: string } | null = null;
    maxDate: Date = new Date();
    searchText: string = '';
    repport: RapportDto = new RapportDto();

    joursMap: { [key: string]: string } = {
        Monday: 'Lundi',
        Tuesday: 'Mardi',
        Wednesday: 'Mercredi',
        Thursday: 'Jeudi',
        Friday: 'Vendredi',
        Saturday: 'Samedi',
        Sunday: 'Dimanche'
    };

    constructor(
        private toastr: ToastrService,
        private service: ImportExportService,
        private missionService: MissionService,
        private planificationService: PlanificationService,
        private dataManagementService: DataManagementService
    ) {
        this.repport.type = 'PATH_APPRO_FUEL_POI_DANONE';
    }

    async ngOnInit(): Promise<void> {
        this.resetPlanning();
        await this.loadGroupsAndVehicles(); // Wait for groups and vehicles to load
        this.loadMissions();
        this.getAllVehiclePlanifications();

        const toDay = new Date();
        this.repport.dateInterval.startDate = new Date(
            toDay.getFullYear(),
            toDay.getMonth(),
            toDay.getDate(),
            0,
            0,
            0,
            1
        );
        this.repport.dateInterval.endDate = new Date(
            toDay.getFullYear(),
            toDay.getMonth(),
            toDay.getDate(),
            23,
            59,
            59,
            59
        );
    }

    loadMissions(): void {
        this.loading = true;
        this.missionService.getMissions().subscribe({
            next: (missions) => {
                this.availableMissions = missions;
                this.loading = false;
            },
            error: () => {
                this.toastr.error('Erreur lors du chargement des missions', 'Erreur');
                this.loading = false;
            }
        });
    }

    getAllVehiclePlanifications(): void {
        this.loading = true;
        this.planificationService.getAllVehiclePlanifications().subscribe({
            next: (allPlanifications) => {
                this.trucks = allPlanifications;
                this.selectedTruck = allPlanifications.length > 0 ? allPlanifications[0] : null;
                this.loading = false;
            },
            error: () => {
                this.toastr.error('Erreur lors du chargement des planifications', 'Erreur');
                this.loading = false;
            }
        });
    }

    async loadGroupsAndVehicles(): Promise<void> {
        this.loading = true;
        return new Promise((resolve, reject) => {
            this.dataManagementService.getAllGroupsDetails('').subscribe({
                next: (res) => {
                    this.groups = res.filter((group) => group.vehicules.length > 0);
                    this.mapGroupsToItems(this.groups);
                    this.getVehicules();
                    this.loading = false;
                    resolve();
                },
                error: () => {
                    this.toastr.error('Erreur lors du chargement des groupes', 'Erreur');
                    this.loading = false;
                    reject();
                }
            });
        });
    }

    mapGroupsToItems(groups: Group[]): void {
        this.groupsItems = groups.map(group => ({
            id: group.idGroupe,
            text: group.nameGroupParent ? `${group.nameGroupParent} / ${group.nom}` : group.nom
        }));
    }

    getVehicules(): void {
        this.vehicules = [];
        this.groups.forEach(group => {
            group.vehicules.forEach(vehicule => {
                if (this.checkIfVehiculeExists(vehicule)) {
                    this.vehicules.push(vehicule);
                }
            });
        });
        this.vehiculesToItems(this.vehicules, 0);
    }

    checkIfVehiculeExists(vehicule: Vehicule): boolean {
        return !this.vehicules.some(v => v.idVehicule === vehicule.idVehicule);
    }

    vehiculesToItems(vehicules: Vehicule[], idGroup: number): void {
        this.vehiculesItems = vehicules.map(vehicule => ({
            id: vehicule.idVehicule,
            matricule: vehicule.matricule,
            subMark: vehicule.subMark || '' // Ensure subMark is always a string
        }));
    }

    selectedGroups(group: { id: number; text: string } | null): void {
        this.selectedGroupsItems = group;
        const selectedGroup = this.groups.find(g => g.idGroupe === group?.id);
        if (selectedGroup) {
            this.vehiculesToItems(selectedGroup.vehicules, selectedGroup.idGroupe);
        } else {
            this.vehiculesToItems(this.vehicules, 0);
        }
        this.vehiculesSelected = null;
        this.newPlanning.idVehicule = null;
    }

    selectedVehicules(vehicule: { id: number; matricule: string; subMark: string } | null): void {
        this.vehiculesSelected = vehicule || { id: 0, matricule: '', subMark: '' }; // Default if null
        this.newPlanning.idVehicule = this.vehiculesSelected.id || null;
    }

    getPlannedDays(missions: { [key: string]: number | null }): number {
        return Object.values(missions).filter(mission => mission !== null).length;
    }

    getMissionLabel(missionId: number | null): string {
        if (!missionId) return 'Aucune mission';
        const mission = this.availableMissions.find(m => m.id === missionId);
        return mission ? mission.label : 'Aucune mission';
    }

    getMissionColor(missionId: number | null): string {
        if (!missionId) return 'grey';
        const mission = this.availableMissions.find(m => m.id === missionId);
        const label = mission ? mission.label : 'Aucune mission';
        const colors: { [key: string]: string } = {
            'Mission A': 'green',
            'Mission B': 'orange',
            'Mission C': 'dodgerblue',
            'Mission D': 'red'
        };
        return colors[label] || 'grey';
    }

    resetPlanning(): void {
        this.newPlanning = { truckId: null, label: '', idVehicule: null, missions: {} };
        this.days.forEach(day => (this.newPlanning.missions[day] = null));
        this.vehiculesSelected = { id: 0, matricule: '', subMark: '' }; // Initialize to avoid null
        this.selectedGroupsItems = null;
        this.planificationAssignmentAddDto = {
            dateDebut: undefined,
            dateFin: undefined,
            id: 0,
            idVehicule: 0,
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false,
            label: ''
        };
    }

    openPlanningModal(): void {
        this.mode = 'ADD';
        this.resetPlanning();
        this.planningModal.show();
    }

    editPlanning(truck: Truck): void {
        this.mode = 'UPDATE';
        this.selectedTruck = truck;
        this.newPlanning = {
            truckId: truck.id,
            label: truck.label,
            idVehicule: truck.idVehicule,
            missions: { ...truck.missions }
        };

        const vehicle = this.vehicules.find(v => v.idVehicule === truck.idVehicule);
        this.vehiculesSelected = vehicle ? { id: vehicle.idVehicule, matricule: vehicle.matricule, subMark: vehicle.subMark || '' } : { id: 0, matricule: '', subMark: '' };

        const group = this.groups.find(g => g.vehicules.some(v => v.idVehicule === truck.idVehicule));
        this.selectedGroupsItems = group ? { id: group.idGroupe, text: group.nameGroupParent ? `${group.nameGroupParent} / ${group.nom}` : group.nom } : null;

        this.planificationAssignmentAddDto = {
            dateDebut: truck.dateDebut ? new Date(truck.dateDebut) : undefined,
            dateFin: truck.dateFin ? new Date(truck.dateFin) : undefined,
            id: truck.id,
            idVehicule: truck.idVehicule,
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false,
            label: truck.label
        };
        this.planningModal.show();
    }

    closePlanningModal(): void {
        this.planningModal.hide();
        this.resetPlanning();
    }

    savePlanning(): void {
        if (!this.isFormValid()) {
            this.toastr.error('Veuillez remplir tous les champs requis', 'Erreur');
            return;
        }

        this.loading = true;
        const truckData: Truck = {
            id: this.mode === 'ADD' ? 0 : this.newPlanning.truckId!,
            label: this.newPlanning.label,
            idVehicule: this.newPlanning.idVehicule!,
            matricule: this.vehiculesSelected!.matricule,
            subMark: this.vehiculesSelected!.subMark,
            missions: { ...this.newPlanning.missions },
            updatedAt: new Date(),
            dateDebut: this.planificationAssignmentAddDto.dateDebut ? this.planificationAssignmentAddDto.dateDebut : null,
            dateFin: this.planificationAssignmentAddDto.dateFin ? this.planificationAssignmentAddDto.dateFin : null
        };

        this.planificationService.createTruckMission(truckData).subscribe({
            next: (savedTruck) => {
                this.toastr.success(`Planification ${this.mode === 'ADD' ? 'ajoutée' : 'mise à jour'}`, 'Succès');
                this.getAllVehiclePlanifications();
                this.loading = false;
                this.closePlanningModal();
            },
            error: () => {
                this.toastr.error('Erreur lors de l\'enregistrement de la planification', 'Erreur');
                this.loading = false;
            }
        });
    }

    isFormValid(): boolean {
        return !!this.newPlanning.label && !!this.newPlanning.idVehicule && !!this.planificationAssignmentAddDto.dateDebut;
    }

    onConfirmDelete(): void {
        if (this.selectedTruck) {
            this.truckDeleteModal.show();
        }
    }

    ExportRapportConfirm(): void {
        if (this.selectedTruck) {
            this.truckGenerateModal.show();
        }
    }

    onDelete(): void {
        if (!this.selectedTruck) return;
        this.loading = true;
        this.planificationService.deleteTruck(this.selectedTruck.id).subscribe({
            next: () => {
                this.toastr.success('Planification supprimée', 'Succès');
                this.getAllVehiclePlanifications();
                this.loading = false;
                this.truckDeleteModal.hide();
                this.selectedTruck = this.trucks.length > 0 ? this.trucks[0] : null;
            },
            error: () => {
                this.toastr.error('Erreur lors de la suppression de la planification', 'Erreur');
                this.loading = false;
                this.truckDeleteModal.hide();
            }
        });
    }

    exportExel(): void {
        this.loading = true;
        /* this.repport.rapportPayloadDto.allVehicules = true;
        this.repport.rapportPayloadDto.byGroups = false;
        this.repport.rapportPayloadDto.byVehicules = false;
        this.service.getRepport(this.repport).subscribe(
            (blob) => {
                if (blob.size > 0) {
                    importedSaveAs(
                        blob,
                        'Rapport ' + this.service.getReportName(this.repport.type) + '.xlsx'
                    );
                } else {
                    this.toastr.warning('Aucune donnée entre ces deux dates !', 'Error');
                }
                this.loading = false;
            },
            (error) => {
                this.toastr.warning('Aucune donnée entre ces deux dates !', 'Error');
                this.loading = false;
            }
        ); */

        const request:MissionSuiviRequest = {dateInterval:this.repport.dateInterval , planId:this.selectedTruck.id,
            timezoneOffset:new Date().getTimezoneOffset(),all:false}
        this.planificationService.getSuiviMissionExcel(request).subscribe(
            (blob) => {
                if (blob.size > 0) {
                    importedSaveAs(blob,'Suivi ' + this.selectedTruck.matricule + '.xlsx');
                } else {
                    this.toastr.warning('Aucune donnée entre ces deux dates !', 'Error');
                }
                this.loading = false;
            },
            () => {
                this.toastr.warning('Aucune donnée entre ces deux dates !', 'Error');
                this.loading = false;
            }
        );
    }

    onChangeStartDate(): void {
        if (
            this.planificationAssignmentAddDto.dateFin &&
            this.planificationAssignmentAddDto.dateDebut &&
            this.planificationAssignmentAddDto.dateFin < this.planificationAssignmentAddDto.dateDebut
        ) {
            this.planificationAssignmentAddDto.dateFin = undefined;
            this.toastr.warning('La date de fin doit être postérieure à la date de début', 'Attention');
        }
    }

    filterTrucks(): Truck[] {
        if (!this.searchText) return this.trucks;
        return this.trucks.filter(truck =>
            truck.matricule.toLowerCase().includes(this.searchText.toLowerCase()) ||
            truck.label.toLowerCase().includes(this.searchText.toLowerCase())
        );
    }

    compareGroups = (item1: any, item2: any) => item1 && item2 && item1.id === item2.id;
    compareVehicules = (item1: any, item2: any) => item1 && item2 && item1.id === item2.id;
}